<template>
  <BaseRoundButton
    v-if="hasPermissions"
    class="document-permissions"
    :class="{ 'is-restricted': hasRestrictions }"
    :icon="hasRestrictions ? 'shield-off' : 'shield'"
    :size="28"
    is-transparent
    tooltip-placement="bottom"
    :aria-label="title"
    data-test="document-permissions"
  >
    <template #tooltip>
      <div class="document-permissions__tooltip">
        <div class="document-permissions__title">{{ title }}</div>
        <ul class="document-permissions__list">
          <li
            v-for="row in rows"
            :key="row.key"
            class="document-permissions__row"
            :class="{ 'is-restricted': !row.allowed }"
            :data-test="`document-permission-${row.key}`"
          >
            <BaseIcon :name="row.allowed ? 'check' : 'x'" class="document-permissions__status" />
            <span class="document-permissions__label">
              {{ $t(`page.analysis.document-permissions.${row.labelKey}`) }}
            </span>
            <span class="document-permissions__value">
              {{ $t(`page.analysis.document-permissions.${row.allowed ? 'allowed' : 'restricted'}`) }}
            </span>
          </li>
          <li class="document-permissions__row is-info" data-test="document-permission-ownerPasswordProtected">
            <BaseIcon :name="ownerPasswordProtected ? 'lock' : 'unlock'" class="document-permissions__status" />
            <span class="document-permissions__label">
              {{ $t('page.analysis.document-permissions.owner-password') }}
            </span>
            <span class="document-permissions__value">
              {{ $t(ownerPasswordProtected ? 'basic-words.yes' : 'basic-words.no') }}
            </span>
          </li>
        </ul>
      </div>
    </template>
  </BaseRoundButton>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon.vue';
import BaseRoundButton from '@/components/base/BaseRoundButton.vue';
import {
  getDocumentPermissionRows,
  hasDocumentPermissions,
  hasRestrictedDocumentPermissions,
  isDocumentOwnerPasswordProtected,
} from '@/util/document-permissions-util';

// Permission flag -> i18n suffix under `page.analysis.document-permissions`.
const LABEL_KEYS = {
  copyAllowed: 'copy',
  formFillAllowed: 'form-fill',
  annotationAllowed: 'annotation',
  textExtractionAllowed: 'text-extraction',
};

/**
 * Header badge summarizing what the PDF's security settings allow (`OpportunityDocument.permissions`).
 * Renders nothing when the document carries no permissions (non-PDF or uploaded before capture existed).
 */
export default {
  name: 'DocumentPermissions',
  components: { BaseIcon, BaseRoundButton },
  props: {
    document: {
      type: Object,
      default: null,
    },
  },
  computed: {
    hasPermissions() {
      return hasDocumentPermissions(this.document);
    },
    hasRestrictions() {
      return hasRestrictedDocumentPermissions(this.document);
    },
    ownerPasswordProtected() {
      return isDocumentOwnerPasswordProtected(this.document);
    },
    rows() {
      return getDocumentPermissionRows(this.document).map((row) => ({ ...row, labelKey: LABEL_KEYS[row.key] }));
    },
    title() {
      return this.$t(
        this.hasRestrictions
          ? 'page.analysis.document-permissions.title-restricted'
          : 'page.analysis.document-permissions.title',
      );
    },
  },
};
</script>

<style lang="scss">
.document-permissions {
  // The root class lands on the BaseRoundButton wrapper; tint the transparent button when restricted.
  &.is-restricted .base-round-button.is-transparent {
    color: $color-text-warning;

    &:hover {
      color: $color-semantic-warning;
    }
  }

  // The popover content is portaled to <body>, so these selectors stay flat.
  &__tooltip {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 200px;
    text-align: left;
  }

  &__title {
    font-weight: 600;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 4px;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 6px;

    &.is-restricted .document-permissions__status {
      color: $color-semantic-error;
    }

    &.is-info .document-permissions__status {
      color: $color-white;
    }
  }

  &__status {
    color: $color-semantic-success;
    flex-shrink: 0;
    height: 12px;
    width: 12px;
  }

  &__label {
    flex: 1;
  }

  &__value {
    opacity: 0.7;
  }
}
</style>
